import React, { useRef } from "react";
import styled from "styled-components";

const Proof = styled.p`
  margin: 16px 0 0;
  padding-left: 12px;
  border-left: 2px solid ${({ theme }) => theme.orange};
  color: ${({ theme }) => theme.biome.projects.ink};
  font-size: 13px;
  line-height: 1.5;
  font-weight: 700;
`;

const OpenButton = styled.button`
  align-self: flex-start;
  margin-top: 16px;
  padding: 9px 12px;
  border: 1px solid ${({ theme }) => theme.orange};
  background: transparent;
  color: ${({ theme }) => theme.orange};
  font: 700 12px ${({ theme }) => theme.fonts.ui};
  cursor: pointer;

  &:hover { background: rgba(255, 130, 54, 0.12); }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.orange}; outline-offset: 3px; }
`;

const Dialog = styled.dialog`
  width: min(720px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px);
  box-sizing: border-box;
  margin: auto;
  padding: 0;
  overflow-y: auto;
  border: 2px solid ${({ theme }) => theme.orange};
  background: ${({ theme }) => theme.biome.projects.card};
  color: ${({ theme }) => theme.biome.projects.ink};

  &::backdrop { background: rgba(0, 0, 0, 0.82); }

  header {
    position: sticky;
    top: 0;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    padding: 22px 26px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    background: ${({ theme }) => theme.biome.projects.card};
  }

  h2 { margin: 0; font-size: clamp(22px, 4vw, 30px); line-height: 1.15; }
  header button {
    padding: 7px 10px;
    border: 1px solid currentColor;
    background: transparent;
    color: inherit;
    cursor: pointer;
  }
  header button:focus-visible { outline: 2px solid ${({ theme }) => theme.orange}; outline-offset: 3px; }

  article { padding: 6px 26px 26px; }
  section { margin-top: 22px; }
  h3 {
    margin: 0 0 8px;
    color: ${({ theme }) => theme.orange};
    font-size: 13px;
    letter-spacing: 0.5px;
  }
  p {
    margin: 0;
    color: ${({ theme }) => theme.biome.projects.muted};
    font-size: 15px;
    line-height: 1.65;
    text-align: justify;
    hyphens: auto;
  }

  @media (max-width: 640px) {
    header { padding: 18px 16px; }
    article { padding: 4px 16px 20px; }
  }
`;

const CaseNotes = ({ project }) => {
  const dialog = useRef(null);
  const { evidence } = project;
  if (!evidence) return null;

  return (
    <>
      <Proof>{evidence.result}</Proof>
      <OpenButton type="button" onClick={() => dialog.current.showModal()}>How I approached it</OpenButton>
      <Dialog ref={dialog} aria-labelledby={`approach-${project.id}`} onClick={(event) => {
        if (event.target === dialog.current) dialog.current.close();
      }}>
        <header>
          <h2 id={`approach-${project.id}`}>{project.title}</h2>
          <button type="button" onClick={() => dialog.current.close()} aria-label="Close approach">Close</button>
        </header>
        <article>
          <section><h3>The problem</h3><p>{evidence.challenge}</p></section>
          <section><h3>My approach</h3><p>{evidence.decision}</p></section>
          <section><h3>What I measured</h3><p>{evidence.result}</p></section>
          <section><h3>What I would improve</h3><p>{evidence.limit}</p></section>
        </article>
      </Dialog>
    </>
  );
};

export default CaseNotes;
